import { Alert, Checkbox, Form, type FormInstance } from "antd";
import { CustomMessage } from "components/PaymentsLanding/SettingsModal/CustomMessage";
import { FormItemGroup } from "@vyne-shared/components/vyneForm/items/VyneFormItems";
import { type Inputs, PaymentTextMessage } from "utils/paymentTextMessage";
import { RequestChannel } from "@vyne-shared/api/VynePayments/responses";
import { type StrictlyDollars, stringFromDollars } from "@vyne-shared/utils/currency";
import { useAuth } from "auth/useAuth";
import { useGetProviderSettingsPatient } from "api/clients/useGetProviderSettingsPatient";
import type { ExistingPatient } from "../existingPatient";
import type { PatientUpdateForm } from "api/requests";
import type { ProviderSettingsPatientResponse } from "api/responses";

function renderMessage(data: ProviderSettingsPatientResponse, inputs: Inputs) {
    let template = data.paymentTextMessageDefaultTemplate;
    if (data.paymentTextMessageKind === "Custom") {
        template = data.paymentTextMessageTemplate;
    }

    const messageBuilder = new PaymentTextMessage(template);
    return messageBuilder.getTextMessage(inputs);
}

function usePlaceholders(form: FormInstance<PatientUpdateForm>, existingPrp: ExistingPatient) {
    const firstName = Form.useWatch("givenName", form) ?? existingPrp?.givenName ?? "";
    const amountDue = useRenderableAmount(form);
    const loginResult = useAuth((state) => state.loginResult);

    if (loginResult) {
        return {
            firstName,
            amountDue,
            providerName: loginResult.customerName,
            paymentLink: "[[Payment Link]]",
        } satisfies Inputs;
    }
}

function useRenderableAmount(form: FormInstance<PatientUpdateForm>) {
    let amount = Form.useWatch("amount", form);

    // amount may be blank to start
    if (!amount) {
        return "$TBD";
    }

    if (typeof amount !== "number") {
        amount = parseFloat(amount) as StrictlyDollars;
    }

    return stringFromDollars(amount);
}

interface MessageAreaProps {
    /** Existing patient details, if any. Will be displayed as default values. */
    readonly existingPrp: ExistingPatient;

    /** The underlying form to store data in. */
    readonly form: FormInstance<PatientUpdateForm>;
}

/** Build the area of the form showing a message preview with customization. */
export const MessageArea = ({ existingPrp, form }: MessageAreaProps) => {
    const requestChannel = Form.useWatch("requestChannel", form);
    const inputs = usePlaceholders(form, existingPrp);

    const { isPending, data } = useGetProviderSettingsPatient();

    if (requestChannel === RequestChannel.Email || isPending || !data || !inputs) {
        // We can only render and customize SMS messages; email is a formatted HTML document which is far more
        // complicated and we don't have good tools for.
        return null;
    }

    return <EnabledMessageArea form={form} message={renderMessage(data, inputs)} />;
};

interface EnabledMessageAreaProps {
    readonly form: FormInstance<PatientUpdateForm>;
    readonly message: string;
}

const EnabledMessageArea = ({ form, message }: EnabledMessageAreaProps) => {
    const customizeNotification = Form.useWatch("customizeNotification", form);

    return (
        <FormItemGroup legendTitle="Message">
            <Alert type="success" message="Default Message" aria-label="Default Message" description={message} />
            <Form.Item name="customizeNotification" valuePropName="checked">
                <Checkbox>Use custom message</Checkbox>
            </Form.Item>
            {customizeNotification && (
                <CustomMessage
                    formName="newPaymentRequest"
                    fieldName="customNotification"
                    disabled={!customizeNotification}
                />
            )}
        </FormItemGroup>
    );
};
